import { Plus, Search, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  useFieldArray,
  useWatch,
  type Control,
  type UseFormRegister,
  type UseFormSetValue,
} from "react-hook-form";
import { searchCauseAdvocates } from "../../api/causeApi";
import type { CauseAdvocateFormData, CauseFormData } from "./causeList.types";

interface CauseAdvocatesSectionProps {
  control: Control<CauseFormData>;
  register: UseFormRegister<CauseFormData>;
  setValue: UseFormSetValue<CauseFormData>;
}

const emptyAdvocate = (
  type: CauseAdvocateFormData["type"] = "petitioner",
): CauseAdvocateFormData => ({
  name: "",
  type,
  enrollment: "",
});

const CauseAdvocatesSection = ({
  control,
  register,
  setValue,
}: CauseAdvocatesSectionProps) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "advocates",
  });

  return (
    <div className="space-y-4 lg:col-span-2">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">Advocates</h4>
          <p className="text-xs text-gray-500">
            Search existing advocates by name, or enter details manually.
          </p>
        </div>
        <button
          type="button"
          onClick={() => append(emptyAdvocate("petitioner"))}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
        >
          <Plus className="h-4 w-4" />
          Add Advocate
        </button>
      </div>

      <div className="space-y-3">
        {fields.map((field, index) => (
          <AdvocateRow
            key={field.id}
            index={index}
            canRemove={fields.length > 1}
            control={control}
            register={register}
            setValue={setValue}
            onRemove={() => remove(index)}
          />
        ))}
      </div>
    </div>
  );
};

interface AdvocateSearchResult {
  _id: string;
  name: string;
  username?: string;
  enrollment?: string;
}

interface AdvocateRowProps {
  index: number;
  canRemove: boolean;
  control: Control<CauseFormData>;
  register: UseFormRegister<CauseFormData>;
  setValue: UseFormSetValue<CauseFormData>;
  onRemove: () => void;
}

const AdvocateRow = ({
  index,
  canRemove,
  control,
  register,
  setValue,
  onRemove,
}: AdvocateRowProps) => {
  const [results, setResults] = useState<AdvocateSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const rowRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const name = useWatch({ control, name: `advocates.${index}.name` }) || "";
  const lockedEnrollment = Boolean(
    useWatch({ control, name: `advocates.${index}.lockedEnrollment` }),
  );

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (!rowRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    const keyword = name.trim();
    if (keyword.length < 2) {
      setResults([]);
      setIsOpen(false);
      setFocusedIndex(-1);
      return;
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        setIsSearching(true);
        const res = await searchCauseAdvocates(keyword);
        const nextResults = res?.data?.status ? res.data.data || [] : [];
        setResults(nextResults);
        setIsOpen(nextResults.length > 0);
        setFocusedIndex(nextResults.length > 0 ? 0 : -1);
      } finally {
        setIsSearching(false);
      }
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [name]);

  const handleSelect = (advocate: AdvocateSearchResult) => {
    setValue(`advocates.${index}.name`, advocate.name || "", {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue(`advocates.${index}.enrollment`, advocate.enrollment || "", {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue(`advocates.${index}.userId`, advocate._id, {
      shouldDirty: true,
    });
    setValue(`advocates.${index}.lockedEnrollment`, true, {
      shouldDirty: true,
    });
    setIsOpen(false);
    setFocusedIndex(-1);
  };

  const handleManualNameChange = () => {
    if (!lockedEnrollment) return;

    setValue(`advocates.${index}.userId`, "", { shouldDirty: true });
    setValue(`advocates.${index}.lockedEnrollment`, false, {
      shouldDirty: true,
    });
    setValue(`advocates.${index}.enrollment`, "", {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  useEffect(() => {
    optionRefs.current = optionRefs.current.slice(0, results.length);
  }, [results.length]);

  useEffect(() => {
    if (!isOpen || focusedIndex < 0) return;
    optionRefs.current[focusedIndex]?.scrollIntoView({ block: "nearest" });
  }, [focusedIndex, isOpen]);

  const handleNameKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && event.key === "ArrowDown" && results.length) {
      event.preventDefault();
      setIsOpen(true);
      setFocusedIndex(0);
      return;
    }

    if (!isOpen) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setFocusedIndex((current) =>
          results.length ? (current + 1) % results.length : -1,
        );
        break;

      case "ArrowUp":
        event.preventDefault();
        setFocusedIndex((current) =>
          results.length
            ? current > 0
              ? current - 1
              : results.length - 1
            : -1,
        );
        break;

      case "Enter":
        if (focusedIndex >= 0 && results[focusedIndex]) {
          event.preventDefault();
          handleSelect(results[focusedIndex]);
        }
        break;

      case "Escape":
        event.preventDefault();
        setIsOpen(false);
        setFocusedIndex(-1);
        break;
    }
  };

  return (
    <div
      ref={rowRef}
      className="rounded-xl border border-gray-200 bg-gray-50 p-3"
    >
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[150px_1fr_1fr_auto]">
        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-700">
            Type
          </label>
          <select
            {...register(`advocates.${index}.type`)}
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-0 focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
          >
            <option value="petitioner">Petitioner</option>
            <option value="respondent">Respondent</option>
          </select>
        </div>

        <div className="relative">
          <label className="mb-1 block text-xs font-semibold text-gray-700">
            Advocate Name
          </label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              {...register(`advocates.${index}.name`, {
                onChange: handleManualNameChange,
              })}
              onFocus={() => {
                if (results.length) {
                  setIsOpen(true);
                  setFocusedIndex((current) => (current >= 0 ? current : 0));
                }
              }}
              onKeyDown={handleNameKeyDown}
              className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-9 pr-3 text-sm outline-0 focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
              placeholder="Search or enter advocate name"
            />
          </div>

          {isOpen && (
            <div
              role="listbox"
              className="absolute z-50 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg"
            >
              {results.map((advocate, resultIndex) => (
                <button
                  key={advocate._id}
                  ref={(node) => {
                    optionRefs.current[resultIndex] = node;
                  }}
                  type="button"
                  role="option"
                  aria-selected={focusedIndex === resultIndex}
                  onMouseEnter={() => setFocusedIndex(resultIndex)}
                  onClick={() => handleSelect(advocate)}
                  className={`block w-full px-3 py-2 text-left text-sm ${
                    focusedIndex === resultIndex ? "bg-blue-50" : ""
                  } hover:bg-blue-50`}
                >
                  <span className="block font-semibold text-gray-900">
                    {advocate.name}
                  </span>
                  <span className="text-xs text-gray-500">
                    {advocate.enrollment || advocate.username || "No enrollment"}
                  </span>
                </button>
              ))}
            </div>
          )}

          {isSearching && (
            <p className="mt-1 text-xs text-gray-400">Searching...</p>
          )}
        </div>

        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-700">
            Enrollment
          </label>
          <input
            {...register(`advocates.${index}.enrollment`)}
            disabled={lockedEnrollment}
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-0 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 disabled:bg-gray-100 disabled:text-gray-500"
            placeholder="Enrollment number"
          />
        </div>

        <div className="flex items-end">
          <button
            type="button"
            disabled={!canRemove}
            onClick={onRemove}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-red-100 bg-white text-red-500 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
            title="Remove advocate"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CauseAdvocatesSection;
